
import React from 'react';
import { mdx } from '@mdx-js/react';


export const frontMatter = {
	id: 'config',
	title: 'Product Configs'
};
export const contentTitle = undefined;
export const metadata = {
  "unversionedId": "api-reference/config",
  "id": "api-reference/config",
  "isDocsHomePage": false,
  "title": "Product Configs",
  "description": "All configs for a product are retrieved in a single request. They are divided into arrays corresponding to the different",
  "source": "@site/docs/api-reference/config.md",
  "sourceDirName": "api-reference",
  "slug": "/api-reference/config",
  "permalink": "/docs/api-reference/config",
  "tags": [],
  "version": "current",
  "frontMatter": {
    "id": "config",
    "title": "Product Configs"
  },
  "sidebar": "docs",
  "previous": {
    "title": "Client Data",
    "permalink": "/docs/api-reference/client"
  },
  "next": {
    "title": "Quotes",
    "permalink": "/docs/api-reference/quotes"
  }
};


/* @jsxRuntime classic */
/* @jsx mdx */

export const toc = [{
  value: 'Base Config Structure',
  id: 'base-config-structure',
  children: []
}, {
  value: 'POLICY_FORM',
  id: 'policy_form',
  children: []
}, {
  value: 'ENDORSEMENTS',
  id: 'endorsements',
  children: []
}, {
  value: 'CANCELLATIONS',
  id: 'cancellations',
  children: []
}];

const layoutProps = {
  toc
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`All configs for a product are retrieved in a single request. They are divided into arrays corresponding to the different
options in the sidebar of the product configuration screen. Every config follows the same basic data structure, but the
`}<inlineCode parentName="p">{`spec`}</inlineCode>{` may differ.`}</p>
    <p><strong parentName="p">{`POST:`}</strong>{` `}<inlineCode parentName="p">{`https://api.{ENV}.dais.com/ioi/v3/product/config/all`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-curl"
      }}>{`curl --location --request POST 'http://api.dev.dais.com/ioi/v3/product/config/all' \\
--header 'Authorization: Basic <yourAuthHere>' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "productId": "<productId>"
}'
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "productId": "3847d175-9ac3-440a-8e53-0e6c54bf6546"
}
`}</code></pre>
    <p><strong parentName="p">{`Request fields:`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Required`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`productId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`UUID of the product to retrieve configs for`}</td>
        </tr>
      </tbody>
    </table>
    <br />
    <h3 {...{
      "id": "base-config-structure"
    }}>{`Base Config Structure`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"<config type>": [
  {
    "id": "a74eedf4-94a9-4b82-b19c-2fbc0cc99138",
    "configIdentity": "<config type>",
    "organizationId": "44677a4c-62ed-40b6-b36f-59bca5088f0c",
    "productId": "3847d175-9ac3-440a-8e53-0e6c54bf6546",
    "spec": {<depends on config type>},
  "updatedTimestamp": "2020-03-21T16:08:13.289Z",
  "version": 0,
  "lastUpdatedBy": {...}
  },
  {...}
]
`}</code></pre>
    <p><strong parentName="p">{`Config fields:`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`UUID for this config. Use this value as `}<inlineCode parentName="td">{`configId`}</inlineCode>{` when firing events that require it.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`configIdentity`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Will always be the same as the config type`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`organizationId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`UUID for the organization which owns this config`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`productId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`UUID for the product this config is for`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`spec`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`May contain additional fields which only apply to some config types. See below`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`updatedTimestamp`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Timestamp when this config was last updated`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`version`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Current version number of this config`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`lastUpdatedBy`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Details on the user who last updated this config`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "policy_form"
    }}>{`POLICY_FORM`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`POLICY_FORM`}</inlineCode>{` array will contain configs for all forms added to a product.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"spec": {
  "name": "Policy Form 1",
  "type": "",
  "formType": "POLICY",
  "productId": "4acaa79e-3645-4fd1-a199-203f8efbd421",
  "ocrMapping": {...}
}
`}</code></pre>
    <p><strong parentName="p">{`Spec fields:`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`name`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Form name`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`type`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`For internal use`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`formType`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Form type. This corresponds to the options available in the IOI when adding a form in product configuration`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`productId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`UUID for this form`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`ocrMapping`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`For internal use`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "endorsements"
    }}>{`ENDORSEMENTS`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`ENDORSEMENTS`}</inlineCode>{` array will contain configs for all endorsement options added to a product.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"spec": {
  "title": "Endorsement 1",
  "description": "Endorsement 1 description",
  "enableEndorsementRules": false,
  "allowEndorsementsInThePast": false
}
`}</code></pre>
    <p><strong parentName="p">{`Spec fields:`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`title`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Name of the endorsement option`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`description`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Description of the endorsement option`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`enableEndorsementRules`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Boolean showing whether eligibility requirements have been enabled for this endorsement option`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`allowEndorsementsInThePast`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Boolean showing whether backdating is enabled for this endorsement option`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "cancellations"
    }}>{`CANCELLATIONS`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`CANCELLATIONS`}</inlineCode>{` array will contain configs for all cancellation reasons added to a product.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"spec": {
    "title": "Cancellation Reason 1",
    "description": "Description of cancellation reason 1.",
    "cancellationConfigId": "687c6334-8305-46be-9474-9ea410d50181",
    "cancellationProductId": "70394ed0-bc6c-4573-8f62-60e93d747cf7",
    "enableCancellationRules": false,
    "enableCancellationProduct": true
}
`}</code></pre>
    <p><strong parentName="p">{`Spec fields:`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`title`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Name of the cancellation reason`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`description`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Description of the cancellation reason`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`cancellationConfigId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`UUID of the cancellation form config `}<inlineCode parentName="td">{`id`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`cancellationProductId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`UUID of the cancellation form `}<inlineCode parentName="td">{`productId`}</inlineCode>{`. This corresponds to `}<inlineCode parentName="td">{`productId`}</inlineCode>{` in the associated form's `}<inlineCode parentName="td">{`spec`}</inlineCode>{` in the `}<inlineCode parentName="td">{`POLICY_FORM`}</inlineCode>{` array.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`enableCancellationRules`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Boolean showing whether cancellation rules have been enabled`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`enableCancellationProduct`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Boolean showing whether a cancellation product (form) has been added to the cancellation reason`}</td>
        </tr>
      </tbody>
    </table>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;
